import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, UserPlus, Clock, ShieldCheck, Activity, Ticket,
  RefreshCw, Heart, Headphones, BookOpen, MessageSquare, Brain,
  CheckCircle2, XCircle, AlertTriangle, Loader2, Copy, Play, Pause,
  Trash2, ChevronRight, X, Zap, Server
} from 'lucide-react';
import { BACKEND_URL } from 'apiConfig';

const API = BACKEND_URL;
const getToken = () => localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

async function apiFetch(path) {
  const res = await fetch(`${API}/api${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}
async function apiPost(path, body = {}) {
  const res = await fetch(`${API}/api${path}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-gray-900' }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-1" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
    <div className="flex items-center gap-2 text-xs text-gray-500 font-medium uppercase tracking-wide">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className={`text-2xl font-bold ${color}`}>{value ?? '-'}</div>
    {sub && <div className="text-xs text-gray-400">{sub}</div>}
  </div>
);

const HealthDot = ({ ok }) => (
  <span className={`inline-block h-2.5 w-2.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
);

// ─── TABS ────────────────────────────────────────────────────────
const TABS = ['overview', 'users', 'queues', 'invites', 'health', 'usage', 'demographics'];

export default function BetaAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [usage, setUsage] = useState(null);
  const [reliability, setReliability] = useState(null);
  const [aiHealth, setAiHealth] = useState(null);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [inviteCount, setInviteCount] = useState(1);
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [demographics, setDemographics] = useState(null);
  const [queueStats, setQueueStats] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [d, f, u, r] = await Promise.all([
        apiFetch('/beta-admin/dashboard'),
        apiFetch('/beta-admin/funnel'),
        apiFetch('/beta-admin/feature-usage'),
        apiFetch('/beta-admin/reliability'),
      ]);
      setDashboard(d); setFunnel(f); setUsage(u); setReliability(r);
    } catch { toast.error('Failed to load dashboard'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refresh = async () => { setRefreshing(true); await loadAll(); setRefreshing(false); toast.success('Refreshed'); };

  const loadUsers = async (filter) => {
    try {
      const path = filter ? `/beta-admin/users?beta_status=${filter}` : '/beta-admin/users';
      const data = await apiFetch(path);
      setUsers(data.users || []);
    } catch { toast.error('Failed to load users'); }
  };

  const loadInvites = async () => {
    try {
      const data = await apiFetch('/beta-admin/invites');
      setInvites(data.invites || []);
    } catch {
      toast.error('Failed to load invites');
    }
  };

  const loadAiHealth = async () => {
    try { setAiHealth(await apiFetch('/beta-admin/ai-health')); } catch {}
  };

  const loadDemographics = async () => {
    try { setDemographics(await apiFetch('/beta-admin/demographics')); } catch {}
  };

  const loadQueueStats = async () => {
    try { setQueueStats(await apiFetch('/beta-admin/queue-stats')); } catch {}
  };

  const handleChangeTier = async (userId, tier, trialDays = null) => {
    try {
      const body = { user_id: userId, tier };
      if (trialDays) body.trial_days = trialDays;
      await apiPost('/beta-admin/change-tier', body);
      toast.success(`User tier changed to ${tier}`);
      loadUsers(statusFilter);
    } catch { toast.error('Failed to change tier'); }
  };

  useEffect(() => {
    if (tab === 'users') loadUsers(statusFilter);
    if (tab === 'invites') loadInvites();
    if (tab === 'health') loadAiHealth();
    if (tab === 'demographics') loadDemographics();
    if (tab === 'queues') loadQueueStats();
  }, [tab, statusFilter]);

  const handleAction = async (action, uid, email) => {
    try {
      await apiPost(`/beta-admin/${action}`, { user_id: uid });
      toast.success(`User ${action}d`);
      loadUsers(statusFilter);
      loadAll();
    } catch { toast.error(`Failed to ${action} user`); }
  };

  const createInvites = async () => {
    setCreating(true);
    try {
      const data = await apiPost('/beta-admin/invite', { count: inviteCount });
      toast.success(`${data.codes.length} invite code(s) created`);
      // Refresh the full invites list from server
      await loadInvites();
      loadAll();
    } catch { toast.error('Failed to create invites'); }
    setCreating(false);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  const openUserDetail = async (uid) => {
    setSelectedUser(uid);
    try { setUserDetail(await apiFetch(`/beta-admin/user/${uid}`)); } catch { toast.error('Failed to load user'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="beta-admin-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/home')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-lg font-bold text-gray-900">Beta Admin</h1>
            <Badge className="bg-blue-100 text-blue-700 text-xs">Founder</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto pb-0">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* ─── OVERVIEW ─── */}
        {tab === 'overview' && dashboard && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard icon={Ticket} label="Pending Invites" value={dashboard.pending_invites} />
              <StatCard icon={Users} label="Active Beta" value={dashboard.active_beta} sub={`/ ${dashboard.active_capacity} capacity`} color="text-green-700" />
              <StatCard icon={Clock} label="Waitlist" value={dashboard.waitlist} sub={`/ ${dashboard.waitlist_capacity} capacity`} />
              <StatCard icon={ShieldCheck} label="Verified" value={dashboard.verified_clinician} />
              <StatCard icon={Activity} label="7-Day Active" value={dashboard.active_7d} color="text-blue-700" />
              <StatCard icon={Heart} label="14-Day Engaged" value={dashboard.engaged_14d} color="text-purple-700" />
            </div>

            {/* Funnel */}
            {funnel && (
              <Card>
                <CardHeader><CardTitle className="text-base">Activation Funnel</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {[
                      ['Signed Up', funnel.signup_completed],
                      ['Email Verified', funnel.email_verified],
                      ['Work Email Verified', funnel.work_email_verified],
                      ['Onboarding Done', funnel.onboarding_completed],
                      ['First Digest', funnel.first_digest_generated],
                      ['First Article Saved', funnel.first_article_saved],
                      ['First Audio Played', funnel.first_audio_played],
                      ['First Deep Dive', funnel.first_deepdive_used],
                      ['First Community Post', funnel.first_community_post],
                    ].map(([label, val]) => (
                      <div key={label} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                        <span className="text-sm text-gray-700">{label}</span>
                        <span className="text-sm font-semibold text-gray-900">{val}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ─── USERS ─── */}
        {tab === 'users' && (
          <>
            <div className="flex gap-2 flex-wrap">
              {['', 'active_beta', 'waitlist', 'paused', 'removed'].map(f => (
                <Button key={f} size="sm" variant={statusFilter === f ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(f)} data-testid={`filter-${f || 'all'}`}>
                  {f || 'All'}
                </Button>
              ))}
            </div>
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">User</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Tier</th>
                      <th className="px-4 py-3 text-left">Verified</th>
                      <th className="px-4 py-3 text-left">Created</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {users.map(u => (
                      <tr key={u.user_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openUserDetail(u.user_id)}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{u.full_name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{u.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            u.beta_status === 'active_beta' ? 'bg-green-100 text-green-700' :
                            u.beta_status === 'waitlist' ? 'bg-yellow-100 text-yellow-700' :
                            u.beta_status === 'paused' ? 'bg-orange-100 text-orange-700' :
                            u.beta_status === 'removed' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }>{u.beta_status || 'none'}</Badge>
                        </td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1">
                            {['free', 'verified', 'premium'].map(t => (
                              <button key={t} onClick={() => handleChangeTier(u.user_id, t, t === 'premium' ? 30 : null)}
                                className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${
                                  u.plan_tier === t || (t === 'premium' && u.trial_active)
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-500 border-gray-200 hover:border-blue-300'
                                }`}
                                data-testid={`tier-${t}-${u.user_id}`}
                              >
                                {t === 'premium' ? 'Pro' : t.charAt(0).toUpperCase() + t.slice(1)}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {u.is_verified ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-gray-300" />}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{u.created_at?.slice(0,10)}</td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1 justify-end">
                            {u.beta_status !== 'active_beta' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAction('activate', u.user_id)} data-testid={`activate-${u.user_id}`}>
                                <Play className="h-3 w-3 mr-1" /> Activate
                              </Button>
                            )}
                            {u.beta_status === 'active_beta' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-orange-600" onClick={() => handleAction('pause', u.user_id)} data-testid={`pause-${u.user_id}`}>
                                <Pause className="h-3 w-3 mr-1" /> Pause
                              </Button>
                            )}
                            {u.beta_status !== 'removed' && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => handleAction('remove', u.user_id)} data-testid={`remove-${u.user_id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No users found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ─── QUEUE STATS ─── */}
        {tab === 'queues' && queueStats && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Activity} label="Queues (24h)" value={queueStats.queues?.last_24h} color="text-blue-700" />
              <StatCard icon={Activity} label="Queues (7d)" value={queueStats.queues?.last_7d} color="text-blue-600" />
              <StatCard icon={Activity} label="Queues (30d)" value={queueStats.queues?.last_30d} />
              <StatCard icon={Activity} label="Total Queues" value={queueStats.queues?.total} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Users} label="Active Profiles" value={queueStats.profiles?.active} color="text-green-700" />
              <StatCard icon={CheckCircle2} label="Emails Sent" value={queueStats.emails?.sent} color="text-emerald-700" />
              <StatCard icon={XCircle} label="Emails Failed" value={queueStats.emails?.failed} color="text-red-600" />
              <StatCard icon={BookOpen} label="Articles Screened" value={queueStats.screening?.total} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatCard icon={Heart} label="Saved to LitHub" value={queueStats.screening?.saved} color="text-sky-700" />
              <StatCard icon={XCircle} label="Skipped" value={queueStats.screening?.skipped} color="text-slate-500" />
            </div>
            {queueStats.specialty_breakdown?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Profiles by Specialty</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {queueStats.specialty_breakdown.map(s => (
                      <div key={s.specialty} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 capitalize">{s.specialty?.replace(/_/g, ' ') || 'Unknown'}</span>
                        <Badge variant="secondary">{s.profiles}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ─── INVITES ─── */}
        {tab === 'invites' && (
          <>
            <Card>
              <CardHeader><CardTitle className="text-base">Generate Invite Codes</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-2 items-end">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Count</label>
                    <Input type="number" min={1} max={20} value={inviteCount} onChange={e => setInviteCount(Math.min(20, Math.max(1, +e.target.value)))}
                      className="w-20" data-testid="invite-count-input" />
                  </div>
                  <Button onClick={createInvites} disabled={creating} data-testid="generate-invites-btn">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
                    Generate
                  </Button>
                  <Button variant="outline" size="sm" onClick={loadInvites} data-testid="refresh-invites-btn">
                    <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader><CardTitle className="text-base">All Invite Codes ({invites.length})</CardTitle></CardHeader>
              <CardContent>
                {invites.length === 0 ? (
                  <p className="text-sm text-gray-400">No invite codes created yet.</p>
                ) : (
                  <div className="space-y-2">
                    {invites.map((inv, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100" data-testid={`invite-row-${i}`}>
                        <div className="flex items-center gap-3">
                          <code className="font-mono text-sm tracking-wider font-semibold" data-testid={`invite-code-${i}`}>
                            {inv.invite_code}
                          </code>
                          <Badge variant={inv.status === 'used' ? 'secondary' : 'default'}
                            className={inv.status === 'used' ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}
                            data-testid={`invite-status-${i}`}>
                            {inv.status}
                          </Badge>
                          {inv.email && (
                            <span className="text-xs text-gray-500">→ {inv.email}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {inv.used_by && (
                            <span className="text-xs text-gray-400">
                              Used {inv.used_at ? new Date(inv.used_at).toLocaleDateString() : ''}
                            </span>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => copyCode(inv.invite_code)} data-testid={`copy-code-${i}`}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* ─── HEALTH ─── */}
        {tab === 'health' && (
          <>
            {aiHealth && (
              <Card>
                <CardHeader><CardTitle className="text-base">AI Provider Health</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {['summary', 'deepdive'].map(key => {
                    const h = aiHealth[key];
                    return h ? (
                      <div key={key} className="flex items-center justify-between py-2 border-b border-gray-50">
                        <div>
                          <span className="text-sm font-medium text-gray-800 capitalize">{key}</span>
                          <span className="text-xs text-gray-500 ml-2">{h.provider}/{h.model}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <HealthDot ok={h.reachable} />
                          <span className="text-xs text-gray-500">{h.latency_ms ? `${h.latency_ms}ms` : h.error || 'N/A'}</span>
                        </div>
                      </div>
                    ) : null;
                  })}
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-sm font-medium text-gray-800">Audio TTS</span>
                    <div className="flex items-center gap-2">
                      <HealthDot ok={aiHealth.tts?.configured} />
                      <span className="text-xs text-gray-500">{aiHealth.tts?.provider}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-gray-800">Audio Storage</span>
                    <span className="text-xs text-gray-500">{aiHealth.audio_storage?.backend} {aiHealth.audio_storage?.s3_configured ? '(S3 configured)' : ''}</span>
                  </div>
                </CardContent>
              </Card>
            )}
            {reliability && (
              <Card>
                <CardHeader><CardTitle className="text-base">Last 24 Hours</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(reliability.last_24h || {}).map(([k, v]) => (
                      <div key={k} className="text-center p-2 bg-gray-50 rounded-lg">
                        <div className="text-lg font-bold text-gray-900">{v}</div>
                        <div className="text-xs text-gray-500">{k.replace(/_/g, ' ')}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ─── USAGE ─── */}
        {tab === 'usage' && usage && (
          <Card>
            <CardHeader><CardTitle className="text-base">Feature Usage (All Time)</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {[
                  [BookOpen, 'Digests Generated', usage.digests_generated],
                  [BookOpen, 'Articles Saved', usage.articles_saved],
                  [Headphones, 'Audio Generated', usage.audio_generated],
                  [Headphones, 'Audio Played', usage.audio_played],
                  [Zap, 'Summary Requests', usage.summary_requests],
                  [Brain, 'Deep Dive Sessions', usage.deepdive_sessions],
                  [MessageSquare, 'Community Posts', usage.community_posts],
                  [MessageSquare, 'Comments', usage.community_comments],
                  [Heart, 'Reactions', usage.community_reactions],
                ].map(([Icon, label, val]) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
                    <Icon className="h-4 w-4 mx-auto mb-1 text-gray-400" />
                    <div className="text-lg font-bold text-gray-900">{val}</div>
                    <div className="text-xs text-gray-500">{label}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── DEMOGRAPHICS ─── */}
        {tab === 'demographics' && demographics && (
          <>
            {/* Completion */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard icon={Users} label="Total Users" value={demographics.total_users} />
              <StatCard icon={CheckCircle2} label="Profiles Completed" value={demographics.profiles_completed} />
              <StatCard icon={Activity} label="Completion Rate" value={`${demographics.completion_rate_pct}%`} color="text-green-700" />
            </div>

            {/* Distribution cards */}
            {[
              ['Specialty Distribution', demographics.specialty_distribution],
              ['Top Subspecialties', demographics.top_subspecialties],
              ['Current Stage', demographics.stage_distribution],
              ['Years in Practice', demographics.years_distribution],
              ['Country', demographics.country_distribution],
              ['State / Province', demographics.state_distribution],
              ['Practice Setting', demographics.setting_distribution],
              ['Clinical Environment', demographics.environment_distribution],
            ].map(([title, data]) => data && data.length > 0 ? (
              <Card key={title}>
                <CardHeader><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {data.map((d, i) => (
                      <div key={i} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                        <span className="text-sm text-gray-700">{d.value}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, (d.count / (demographics.profiles_completed || 1)) * 100)}%` }} />
                          </div>
                          <span className="text-xs font-medium text-gray-600 w-6 text-right">{d.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : null)}

            {/* Engagement by specialty */}
            {demographics.engagement_by_specialty?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-sm">Engagement by Specialty</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {demographics.engagement_by_specialty.map((d, i) => (
                      <div key={i} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                        <span className="text-sm text-gray-700">{d.specialty}</span>
                        <span className="text-xs text-gray-500">{d.users} user{d.users !== 1 ? 's' : ''} / {d.total_events} events</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* ─── USER DETAIL DRAWER ─── */}
      {selectedUser && userDetail && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative w-full max-w-md bg-white shadow-xl overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="user-detail-drawer">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
              <h3 className="font-semibold text-gray-900">User Detail</h3>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <div className="text-lg font-semibold">{userDetail.user?.full_name || 'N/A'}</div>
                <div className="text-sm text-gray-500">{userDetail.user?.email}</div>
                <div className="flex gap-2 mt-2">
                  <Badge className={userDetail.user?.beta_status === 'active_beta' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                    {userDetail.user?.beta_status || 'none'}
                  </Badge>
                  {userDetail.verification?.status && (
                    <Badge className="bg-blue-100 text-blue-700">{userDetail.verification.method}: {userDetail.verification.status}</Badge>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[
                  ['Last Login', userDetail.last_login?.slice(0, 10) || 'Never'],
                  ['Digests', userDetail.digest_count],
                  ['Library', userDetail.library_count],
                  ['Audio Uses', userDetail.audio_uses],
                  ['Deep Dives', userDetail.deepdive_uses],
                  ['Summaries', userDetail.summary_uses],
                  ['Posts', userDetail.community_posts],
                  ['Comments', userDetail.community_comments],
                ].map(([label, val]) => (
                  <div key={label} className="bg-gray-50 rounded-lg p-2.5">
                    <div className="text-xs text-gray-500">{label}</div>
                    <div className="font-semibold text-gray-900">{val}</div>
                  </div>
                ))}
              </div>

              {/* Practice Profile */}
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Practice Profile</span>
                  {userDetail.practice_profile_complete ? (
                    <Badge className="bg-green-100 text-green-700 text-xs">{userDetail.practice_field_count} fields</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-500 text-xs">Not completed</Badge>
                  )}
                </div>
                {userDetail.practice_profile && (
                  <div className="space-y-1 text-xs text-gray-600">
                    {Object.entries(userDetail.practice_profile).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-gray-400">{k.replace(/_/g, ' ')}</span>
                        <span className="text-gray-700 text-right max-w-[60%] truncate">{Array.isArray(v) ? v.join(', ') : v}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Personalization Signals */}
              {userDetail.personalization_signals?.active && (
                <div className="border-t pt-3">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">Active Personalization</p>
                  <div className="space-y-1">
                    {userDetail.personalization_signals.signals.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">{s.field.replace(/_/g, ' ')}</span>
                        <div className="flex items-center gap-1.5">
                          <Badge className={s.weight === 'moderate' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'} style={{fontSize: '10px', padding: '1px 6px'}}>
                            {s.weight}
                          </Badge>
                          <span className="text-gray-700 max-w-[100px] truncate">{Array.isArray(s.value) ? s.value.join(', ') : s.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
